import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { apiClient } from '../api';
import { ChevronDown, Eye, EyeOff, Loader2 } from 'lucide-react';

// "Monolith" sign-in: the DRAGONEYE wordmark is the hero (an amber dragon's
// eye stands in for the O), with sign-in on a light panel beside it.
const INK = '#0B0B0B';
const IVORY = '#F2EFE8';
const PAPER = '#EFEBE3';
const AMBER = '#F5A524';
const MUTED_DARK = '#A8A39A'; // on ink
const MUTED_LIGHT = '#4A453E'; // on paper
const DISPLAY = { fontFamily: 'Anton, Impact, sans-serif', fontWeight: 400 };
const MONO = { fontFamily: "'IBM Plex Mono', monospace" };

// The O in DRAGON: a dragon's eye. Sized in em so it scales with the wordmark.
const EyeGlyph = () => (
  <svg
    role="img"
    aria-label="O"
    viewBox="0 0 150 186"
    className="shrink-0"
    style={{ width: '0.6em', height: '0.744em', margin: '0.072em 0.016em 0' }}
  >
    <rect x="6" y="6" width="138" height="174" rx="69" fill={INK} stroke={IVORY} strokeWidth="12" />
    <circle cx="75" cy="93" r="48" fill={AMBER} />
    <circle cx="75" cy="93" r="48" fill="none" stroke={INK} strokeOpacity="0.35" strokeWidth="10" strokeDasharray="2 5" />
    <path d="M75 50 C86 70 86 116 75 136 C64 116 64 70 75 50 Z" fill={INK} />
    <ellipse cx="60" cy="74" rx="7" ry="4" fill="#FFF6DD" fillOpacity="0.9" transform="rotate(-30 60 74)" />
  </svg>
);

const GoogleMark = () => (
  <span className="w-[26px] h-[26px] rounded-full bg-white flex items-center justify-center shrink-0">
    <svg aria-hidden="true" viewBox="0 0 24 24" className="w-4 h-4">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  </span>
);

const inputCls = 'w-full h-[46px] px-3 bg-white border-[1.5px] rounded-none text-[15px] placeholder-[#8A8378] focus:outline-none';

const Login = () => {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [superadminOpen, setSuperadminOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const redirect = searchParams.get('redirect') || '/';
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    login();
  };

  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // The Worker answers with the session as an HttpOnly cookie.
      await apiClient.post('/auth/login', { email, password });
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: INK, color: IVORY }}>

      {/* ── Wordmark panel ── */}
      <section
        aria-label="About DragonEye"
        className="relative overflow-hidden flex-1 min-w-0 flex flex-col px-6 pt-8 pb-8 sm:px-10 lg:px-16 lg:pt-11 lg:pb-8"
      >
        {/* Faint dragon-scale texture */}
        <svg aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none">
          <defs>
            <pattern id="login-scales" width="40" height="22" patternUnits="userSpaceOnUse">
              <path d="M0 22 A20 20 0 0 1 40 22 M-20 11 A20 20 0 0 1 20 11 M20 11 A20 20 0 0 1 60 11" fill="none" stroke={IVORY} strokeOpacity="0.05" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#login-scales)" />
        </svg>

        <div className="relative flex flex-col">
          <h1
            className="m-0 flex flex-col uppercase text-[88px] sm:text-[130px] lg:text-[170px] xl:text-[220px] 2xl:text-[250px]"
            style={{ ...DISPLAY, lineHeight: 0.86, letterSpacing: '0.005em' }}
          >
            <span className="sr-only">DragonEye</span>
            <span aria-hidden="true" className="flex items-center gap-[0.016em]">Drag<EyeGlyph />n</span>
            <span aria-hidden="true" style={{ color: 'transparent', WebkitTextStroke: `2.5px ${IVORY}` }}>Eye</span>
          </h1>
          <span className="mt-5 text-[11.5px] sm:text-[12.5px] tracking-[0.16em]" style={{ ...MONO, color: MUTED_DARK }}>
            UNIVERSITI MALAYA · EBSI PLATFORM
          </span>
        </div>

        <div className="relative mt-10 lg:mt-auto flex flex-col sm:flex-row sm:items-end justify-between gap-6 lg:gap-10">
          <div className="max-w-[520px] flex flex-col gap-3">
            <h2 className="m-0 text-[22px] lg:text-[26px] font-semibold leading-tight tracking-[-0.01em]">
              Every asset watched. <span style={{ color: AMBER }}>Every indicator checked.</span>
            </h2>
            <p className="hidden sm:block m-0 text-[15px] leading-relaxed" style={{ color: MUTED_DARK }}>
              Agents on your servers, endpoints and network devices report in. DragonEye checks what they see
              against threat intelligence and vulnerability data, and shows what matters on one dashboard.
            </p>
          </div>
          <span className="hidden sm:block shrink-0 text-[12.5px] leading-normal sm:text-right" style={{ color: MUTED_DARK }}>
            Faculty of Computer Science<br />&amp; Information Technology
          </span>
        </div>
      </section>

      {/* ── Sign-in panel ── */}
      <section
        aria-label="Sign in"
        className="w-full lg:w-[440px] shrink-0 flex flex-col px-6 py-8 sm:px-10 lg:px-11 lg:pt-10 lg:pb-8"
        style={{ background: PAPER, color: INK }}
      >
        <div className="flex items-center justify-between">
          <img src="/UMDE_logo.png" alt="UM-DragonEye, Universiti Malaya" className="h-[34px] w-auto" />
          <span className="text-sm" style={{ color: MUTED_LIGHT }}>Help</span>
        </div>

        <div className="w-full max-w-sm mx-auto lg:mx-0 lg:max-w-none flex-1 flex flex-col justify-center gap-[22px] py-10">
          <span className="text-xs tracking-[0.14em]" style={{ ...MONO, color: MUTED_LIGHT }}>SECURE SIGN-IN</span>
          <h2 className="m-0 -mt-2.5 uppercase text-[54px] leading-[0.95]" style={DISPLAY}>Sign in</h2>
          <p className="m-0 -mt-1.5 text-[15px] leading-normal" style={{ color: MUTED_LIGHT }}>
            Use the Google account your organisation admin invited.
          </p>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="w-full h-[52px] flex items-center justify-center gap-3 rounded-none text-[15px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-70"
            style={{ background: INK, color: IVORY }}
          >
            {googleLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Redirecting to Google…</>
            ) : (
              <><GoogleMark />Continue with Google</>
            )}
          </button>

          <div className="h-px" style={{ background: '#CFC8BB' }} />

          {/* Collapsible superadmin sign-in */}
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => setSuperadminOpen(o => !o)}
              aria-expanded={superadminOpen}
              className="w-full h-11 flex items-center justify-between text-sm font-medium hover:opacity-80"
              style={{ color: MUTED_LIGHT }}
            >
              Platform superadmin sign-in
              <ChevronDown className={`w-4 h-4 transition-transform ${superadminOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
            </button>

            {superadminOpen && (
              <form onSubmit={handleCredentialLogin} className="flex flex-col gap-3 mt-1">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sa-email" className="text-[13px] font-medium">Email</label>
                  <input
                    id="sa-email"
                    type="text"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="admin@organisation.com"
                    autoComplete="username"
                    required
                    className={inputCls}
                    style={{ borderColor: INK, color: INK }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sa-pass" className="text-[13px] font-medium">Password</label>
                  <div className="relative">
                    <input
                      id="sa-pass"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      required
                      className={`${inputCls} pr-11`}
                      style={{ borderColor: INK, color: INK }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center"
                      style={{ color: MUTED_LIGHT }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div role="alert" className="text-sm px-3 py-2 border-[1.5px]" style={{ borderColor: '#B4232A', color: '#8F1C22', background: '#FBEAEA' }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-[46px] flex items-center justify-center gap-2 rounded-none text-[15px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ background: AMBER, color: INK }}
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Signing in…</> : 'Sign in as superadmin'}
                </button>
                <p className="m-0 text-xs text-center" style={{ color: MUTED_LIGHT }}>
                  Restricted to platform superadmins. Sessions expire after 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t text-[13px]" style={{ borderColor: '#CFC8BB', color: '#5A544B' }}>
          <span>© {new Date().getFullYear()} Universiti Malaya</span>
          <span className="flex gap-4"><span>Privacy</span><span>Terms</span></span>
        </div>
      </section>
    </div>
  );
};

export default Login;
